import messageHandler from './MessageHandler';
import { getToken } from '@/utils/auth'
class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.baseUrl = process.env.VUE_APP_BASE_WS_URL  + process.env.VUE_APP_BASE_API;
    this.reconnectInterval = 5000;
    this.reconnectTimer = null;
    this.subscriptions = new Set();
    this.messageCallbacks = new Map(); // 按主题存储的回调函数
    this.globalCallbacks = []; // 全局消息回调
    this.connectionCallbacks = [];
    this.disconnectionCallbacks = [];
    this.errorCallbacks = [];
    this.authFailureCallbacks = [];
    this.username = null;
    this.heartbeatTimer = null;
  }

  /**
   * 初始化WebSocket连接（带Token认证）
   * @param {string} token - 认证Token
   */
  init() {
    this.disconnect();
    const token = getToken()
    if (!token) {
      this.triggerAuthFailure('没有找到有效的认证Token，无法重连');
      return;
    }

    try {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const base = this.baseUrl
      const url = `${wsProtocol}${base}/ws/notification?token=${encodeURIComponent(token)}`;
      this.socket = new WebSocket(url);

      this.socket.onopen = (event) => this.handleOpen(event);
      this.socket.onmessage = (event) => this.handleMessage(event);
      this.socket.onclose = (event) => this.handleClose(event);
      this.socket.onerror = (error) => this.handleError(error);
    } catch (error) {
      console.error('WebSocket初始化失败', error);
      this.errorCallbacks.forEach(callback => callback(error));
      this.scheduleReconnect();
    }
  }

  /**
   * 处理连接打开事件
   */
  handleOpen(event) {
    console.log('WebSocket连接成功');
    this.isConnected = true;
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // 启动心跳检测
    this.startHeartbeat();
  }

  /**
   * 处理接收到的消息
   */
  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      // console.log('接收到消息:', message);
      // 处理连接成功消息
      if (message.type === 'CONNECTED') {
        this.username = message.username;
        this.connectionCallbacks.forEach(callback => callback(message));
        this.resubscribe();
        return;
      }
      
      // 使用消息处理器处理消息
      const processedMessage = messageHandler.handleMessage(message);
      
      // 触发全局回调
      this.globalCallbacks.forEach(callback => 
        callback(processedMessage, message)
      );
      
      // 如果有目标主题，触发对应主题的回调
      if (message.destination) {
        const callbacks = this.messageCallbacks.get(message.destination) || [];
        callbacks.forEach(callback => 
          callback(processedMessage, message)
        );
      }
      
    } catch (error) {
      console.error('解析消息失败:', error, '原始数据:', event.data);
      this.errorCallbacks.forEach(callback => callback({
        type: 'PARSE_ERROR',
        message: '消息解析失败',
        originalData: event.data,
        error: error.message
      }));
    }
  }

  /**
   * 处理连接关闭事件
   */
  handleClose(event) {
    console.log('WebSocket连接关闭', event);
    this.isConnected = false;
    this.stopHeartbeat();
    
    this.disconnectionCallbacks.forEach(callback => callback(event));
    
    // 认证失败
    if (event.code === 1008) {
      this.triggerAuthFailure(event.reason || 'WebSocket认证失败');
      return;
    }
    
    // 自动重连
    if (this.socket) {
      this.scheduleReconnect();
    }
  }

  /**
   * 处理错误事件
   */
  handleError(error) {
    console.error('WebSocket错误', error);
    this.errorCallbacks.forEach(callback => callback(error));
  }

  /**
   * 启动心跳检测
   */
  startHeartbeat() {
    this.stopHeartbeat();
    // 每30秒发送一次心跳
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this.send({
          type: 'HEARTBEAT',
          timestamp: Date.now()
        });
      }
    }, 30000);
  }

  /**
   * 停止心跳检测
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 订阅主题 返回取消主体
   * @param {string} topic - 订阅主题
   * @param {function} callback - 消息回调函数
   * @param {object} options - 选项，如消息类型过滤
   */
  subscribe(topic, callback, options = {}) {
    if (!this.messageCallbacks.has(topic)) {
      this.messageCallbacks.set(topic, []);
    }
    
    // 包装回调函数，支持消息类型过滤
    const wrappedCallback = (processedMessage, originalMessage) => {
      if (options.messageTypes && !options.messageTypes.includes(originalMessage.type)) {
        return; // 过滤掉不需要的消息类型
      }
      callback(processedMessage, originalMessage);
    };
    
    this.messageCallbacks.get(topic).push(wrappedCallback);
    
    if (this.isConnected) {
      this.sendSubscribeRequest(topic);
    } else {
      this.subscriptions.add(topic);
      console.log(`将在连接成功后订阅主题: ${topic}`);
    }
    
    // 返回取消订阅函数
    return () => this.unsubscribe(topic, wrappedCallback);
  }

  /**
   * 发送订阅请求
   */
  sendSubscribeRequest(topic) {
    this.send({
      type: 'SUBSCRIBE',
      destination: topic,
      timestamp: Date.now()
    });
  }

  /**
   * 取消订阅
   */
  unsubscribe(topic, callback) {
    if (this.messageCallbacks.has(topic)) {
      const callbacks = this.messageCallbacks.get(topic);
      if (callback) {
        const index = callbacks.indexOf(callback);
        if (index !== -1) {
          callbacks.splice(index, 1);
        }
      } else {
        this.messageCallbacks.set(topic, []);
      }
      
      if (callbacks.length === 0) {
        this.messageCallbacks.delete(topic);
        this.sendUnsubscribeRequest(topic);
      }
    }
    
    this.subscriptions.delete(topic);
  }

  /**
   * 发送取消订阅请求
   */
  sendUnsubscribeRequest(topic) {
    if (this.isConnected) {
      this.send({
        type: 'UNSUBSCRIBE',
        destination: topic,
        timestamp: Date.now()
      });
    }
  }

  /**
   * 发送消息到指定主题
   */
  sendToTopic(topic, payload) {
    return this.send({
      type: 'MESSAGE',
      destination: topic,
      payload: payload,
      timestamp: Date.now()
    });
  }

  /**
   * 发送原始消息
   */
  send(message) {
    if (this.isConnected && this.socket) {
      try {
        this.socket.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('发送消息失败:', error);
        this.errorCallbacks.forEach(callback => callback(error));
        return false;
      }
    } else {
      console.warn('WebSocket未连接，无法发送消息');
      return false;
    }
  }

  /**
   * 重新订阅所有主题
   */
  resubscribe() {
    if (this.isConnected && this.subscriptions.size > 0) {
      console.log('重新订阅所有主题...');
      this.subscriptions.forEach(topic => {
        this.sendSubscribeRequest(topic);
      });
    }
  }

  /**
   * 安排重连
   */
  scheduleReconnect() {
    if (!this.reconnectTimer) {
      console.log(`将在${this.reconnectInterval/1000}秒后尝试重连...`);
      this.reconnectTimer = setTimeout(() => {
        console.log('尝试重连WebSocket...');
        this.init();
      }, this.reconnectInterval);
    }
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.socket) {
      this.socket.close(1000, '正常关闭');
      this.socket = null;
    }
    
    this.isConnected = false;
    this.username = null;
    this.stopHeartbeat();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * 触发认证失败回调
   */
  triggerAuthFailure(reason) {
    console.error('WebSocket认证失败:', reason);
    this.authFailureCallbacks.forEach(callback => callback(reason));
  }

  // 回调注册方法 将返回的消息交由调用方处理
  onConnect(callback) { this.connectionCallbacks.push(callback); }
  onDisconnect(callback) { this.disconnectionCallbacks.push(callback); }
  onError(callback) { this.errorCallbacks.push(callback); }
  onAuthFailure(callback) { this.authFailureCallbacks.push(callback); }
  onGlobalMessage(callback) { this.globalCallbacks.push(callback); }

  /**
   * 获取当前用户名
   */
  getUsername() { return this.username; }
}

export default new WebSocketService();
